import {
  Atom01Icon,
  BrainIcon,
  Bug01Icon,
  ChartHistogramIcon,
  Database01Icon,
  Edit03Icon,
  File01Icon,
  FlashIcon,
  GitBranchIcon,
  HelpCircleIcon,
  HierarchyIcon,
  LibraryIcon,
  LinkSquare02Icon,
  PlayIcon,
  RadioIcon,
  RefreshIcon,
  Search01Icon,
  SearchVisualIcon,
  SecurityCheckIcon,
  Settings01Icon,
  Shield01Icon,
  SourceCodeIcon,
  Target03Icon,
  TerminalIcon,
  UserMultipleIcon,
} from '@terra/ui-icons';
import {
  attackerHitlConfig,
  renderAdGraphVisualizationCard,
  renderAskUserAnswerCard,
  renderAttackerCard,
  renderAttackerContent,
  renderAttackerEditContent,
  renderAttackerEditModal,
  renderAttackerResult,
  renderCoPilotSessionExportCard,
  renderDataQueryCard,
  renderExternalAttackerCard,
  renderExternalNetworkSignalGenerationCard,
  renderFindingsListResult,
  renderMarkdownResult,
  renderObjectUpdateCard,
  renderPortalObjectPresentationCard,
  renderSkillDraftUpdateCard,
  renderThinkingStyleMarkdownResult,
  renderVegaLiteVisualizationCard,
} from './tools';
import {
  EXTERNAL_NETWORK_QUERY_MCP_TOOL_CONFIGS,
  EXTERNAL_NETWORK_SCANNER_MCP_TOOL_CONFIGS,
  EXTERNAL_NETWORK_SCOPE_MCP_TOOL_CONFIGS,
} from './tools/external-network-mcp/external-network-mcp-tool-config';
import { extractCodeAnalyzerMetadata } from './extractors';
import { getSubAgentDisplayInput } from './parsers';
import {
  renderCodeAnalyzerCard,
  renderCodeAnalyzerResult,
  renderCodeAnalyzerStreamingContent,
  renderSubAgentCard,
  renderTraceInvestigationCard,
  renderTraceInvestigationStreamingContent,
} from './renderers';
import { renderFileReadResult, renderFileWriteResult } from './tools/file';
import type { ToolConfig } from './tool-config-types';

export const buildAgentsAndMcpToolConfigs = (): Record<string, ToolConfig> => ({
  'code-analyzer': {
    name: 'Analyze Code',
    icon: GitBranchIcon,
    category: 'intelligence',
    cardType: 'inline',
    runningText: 'Analyzing code...',
    renderCard: renderCodeAnalyzerCard,
  },
  'code-analyzer-tool': {
    name: 'Analyze Code',
    icon: GitBranchIcon,
    category: 'intelligence',
    cardType: 'minimal',
    isStreaming: true,
    hideInput: true,
    hideFromSubAgentSummary: true,
    runningText: 'Analyzing code...',
    extractMetadata: extractCodeAnalyzerMetadata,
    renderCard: renderCodeAnalyzerCard,
    renderStreamingContent: renderCodeAnalyzerStreamingContent,
    renderOutput: renderCodeAnalyzerResult,
  },
  'generate-security-test-case-for-signal': {
    name: 'Draft Test Case',
    icon: SourceCodeIcon,
    category: 'intelligence',
    cardType: 'inline',
    contentType: 'markdown',
    renderOutput: renderMarkdownResult,
    hasExpandableContent: () => false,
  },
  'trace-investigation-tool': {
    name: 'Trace Detective',
    icon: Search01Icon,
    category: 'sub-agent',
    cardType: 'sub-agent',
    hideInput: true,
    isStreaming: true,
    runningText: 'Investigating trace...',
    renderCard: renderTraceInvestigationCard,
    renderStreamingContent: renderTraceInvestigationStreamingContent,
    renderOutput: renderThinkingStyleMarkdownResult,
  },
  trace_investigation: {
    name: 'Trace Detective',
    icon: Search01Icon,
    category: 'sub-agent',
    cardType: 'sub-agent',
    hideInput: true,
    isStreaming: true,
    runningText: 'Investigating trace...',
    renderCard: renderTraceInvestigationCard,
    renderStreamingContent: renderTraceInvestigationStreamingContent,
    renderOutput: renderThinkingStyleMarkdownResult,
  },
  'todo-write': {
    name: 'Update plan',
    icon: Edit03Icon,
    category: 'intelligence',
    cardType: 'minimal',
    hideInput: true,
    hideFromSubAgentSummary: true,
    renderCard: () => null,
  },
  'findings-tool': {
    name: 'Search Findings',
    icon: Bug01Icon,
    category: 'intelligence',
    cardType: 'inline',
    contentType: 'findings-list',
    renderOutput: renderFindingsListResult,
  },
  'signal-latest-execution-summary-tool': {
    name: 'Execution Summary',
    icon: PlayIcon,
    category: 'intelligence',
    cardType: 'inline',
  },
  'write-file': {
    name: 'Write',
    icon: File01Icon,
    category: 'file-operation',
    cardType: 'inline',
    contentType: 'file-write',
    renderOutput: renderFileWriteResult,
  },
  'read-file': {
    name: 'Read',
    icon: File01Icon,
    category: 'file-operation',
    cardType: 'inline',
    contentType: 'file-read',
    renderOutput: renderFileReadResult,
  },
  ls: {
    name: 'List',
    icon: File01Icon,
    category: 'file-operation',
    cardType: 'inline',
  },
  'list-files': {
    name: 'List',
    icon: File01Icon,
    category: 'file-operation',
    cardType: 'inline',
  },
  grep: {
    name: 'Search',
    icon: Search01Icon,
    category: 'file-operation',
    cardType: 'inline',
    runningText: 'Searching code...',
  },
  read: {
    name: 'Read',
    icon: File01Icon,
    category: 'file-operation',
    cardType: 'inline',
    runningText: 'Reading file...',
  },
  glob: {
    name: 'List',
    icon: File01Icon,
    category: 'file-operation',
    cardType: 'inline',
    runningText: 'Listing files...',
  },
  bash: {
    name: 'Command',
    icon: TerminalIcon,
    category: 'file-operation',
    cardType: 'inline',
    runningText: 'Running command...',
  },
  write: {
    name: 'Write',
    icon: File01Icon,
    category: 'file-operation',
    cardType: 'inline',
    runningText: 'Writing file...',
  },
  edit: {
    name: 'Edit',
    icon: Edit03Icon,
    category: 'file-operation',
    cardType: 'inline',
    runningText: 'Editing file...',
  },
  validator: {
    name: 'Validator',
    icon: Shield01Icon,
    category: 'sub-agent',
    cardType: 'sub-agent',
    runningText: 'Validating findings...',
    getDisplayInput: getSubAgentDisplayInput,
    renderCard: renderSubAgentCard,
  },
  operator: {
    name: 'Conductor',
    icon: Atom01Icon,
    category: 'sub-agent',
    cardType: 'sub-agent',
    runningText: 'Executing operations...',
    getDisplayInput: getSubAgentDisplayInput,
    renderCard: renderSubAgentCard,
  },
  intelligence: {
    name: 'Researcher',
    icon: BrainIcon,
    category: 'sub-agent',
    cardType: 'sub-agent',
    runningText: 'Researching...',
    getDisplayInput: getSubAgentDisplayInput,
    renderCard: renderSubAgentCard,
  },
  recon: {
    name: 'Recon',
    icon: BrainIcon,
    category: 'sub-agent',
    cardType: 'sub-agent',
    runningText: 'Reconning the target...',
    getDisplayInput: getSubAgentDisplayInput,
    renderCard: renderSubAgentCard,
  },
  'code-analyst': {
    name: 'Code Analyst',
    icon: SourceCodeIcon,
    category: 'sub-agent',
    cardType: 'sub-agent',
    runningText: 'Analyzing source code...',
    getDisplayInput: getSubAgentDisplayInput,
    renderCard: renderSubAgentCard,
  },
  general: {
    name: 'General',
    icon: FlashIcon,
    category: 'sub-agent',
    cardType: 'sub-agent',
    runningText: 'Working...',
    getDisplayInput: getSubAgentDisplayInput,
    renderCard: renderSubAgentCard,
  },
  'application-data-analyst': {
    name: 'Data Analyst',
    icon: Database01Icon,
    category: 'sub-agent',
    cardType: 'sub-agent',
    runningText: 'Exploring environment...',
    getDisplayInput: getSubAgentDisplayInput,
    renderCard: renderSubAgentCard,
  },
  'object-creator': {
    name: 'Scribe',
    icon: Edit03Icon,
    category: 'sub-agent',
    cardType: 'sub-agent',
    runningText: 'Creating entities...',
    getDisplayInput: getSubAgentDisplayInput,
    renderCard: renderSubAgentCard,
  },
  'background-attack': {
    name: 'Background Attack',
    icon: Target03Icon,
    category: 'sub-agent',
    cardType: 'sub-agent',
    runningText: 'Launching background attack...',
    getDisplayInput: getSubAgentDisplayInput,
    renderCard: renderSubAgentCard,
  },
  'skill-builder': {
    name: 'Skill Builder',
    icon: Edit03Icon,
    category: 'sub-agent',
    cardType: 'sub-agent',
    runningText: 'Building skill...',
    getDisplayInput: getSubAgentDisplayInput,
    renderCard: renderSubAgentCard,
  },
  'attacker-tool': {
    name: 'Attack Simulation',
    icon: Target03Icon,
    category: 'executable',
    cardType: 'minimal',
    contentType: 'attacker',
    hideInput: true,
    hitlConfig: attackerHitlConfig,
    usesTabEdit: true,
    renderContent: renderAttackerContent,
    renderEditModal: renderAttackerEditModal,
    renderEditContent: renderAttackerEditContent,
    renderOutput: renderAttackerResult,
    renderCard: renderAttackerCard,
  },
  'external-attacker-tool': {
    name: 'External Attack Execution',
    icon: FlashIcon,
    category: 'executable',
    cardType: 'minimal',
    hideInput: true,
    renderCard: renderExternalAttackerCard,
  },
  'signal-update': {
    name: 'Update Signal',
    icon: Settings01Icon,
    category: 'executable',
    cardType: 'minimal',
  },
  execution_status_update: {
    name: 'Status Update',
    icon: RefreshIcon,
    category: 'executable',
    cardType: 'minimal',
  },
  search_existing_skills: {
    name: 'Search Skills',
    icon: Search01Icon,
    category: 'intelligence',
    cardType: 'inline',
    contentType: 'json',
    runningText: 'Searching skills...',
  },
  validate_tool_references: {
    name: 'Validate Tool References',
    icon: Settings01Icon,
    category: 'intelligence',
    cardType: 'inline',
    contentType: 'json',
    runningText: 'Validating tool references...',
  },
  run_security_review: {
    name: 'Security Review',
    icon: Shield01Icon,
    category: 'intelligence',
    cardType: 'inline',
    contentType: 'json',
    runningText: 'Reviewing skill safety...',
  },
  propose_skill_draft_update: {
    name: 'Update Skill Draft',
    icon: Edit03Icon,
    category: 'intelligence',
    cardType: 'minimal',
    hideInput: true,
    runningText: 'Updating skill draft...',
    renderCard: renderSkillDraftUpdateCard,
  },
  propose_finding_update: {
    name: 'Update Finding Draft',
    icon: Edit03Icon,
    category: 'intelligence',
    cardType: 'minimal',
    hideInput: true,
    runningText: 'Updating finding draft...',
    renderCard: renderObjectUpdateCard,
  },
  rerun_finding_cvss: {
    name: 'CVSS review',
    icon: Edit03Icon,
    category: 'intelligence',
    cardType: 'minimal',
    hideInput: true,
    runningText: 'Reviewing the CVSS vector...',
    renderCard: renderObjectUpdateCard,
  },
  'manage-custom-roles-tool': {
    name: 'Manage Custom Roles',
    icon: UserMultipleIcon,
    category: 'intelligence',
    cardType: 'minimal',
    hideInput: true,
    runningText: 'Managing custom roles...',
    renderCard: renderObjectUpdateCard,
  },
  present_portal_object: {
    name: 'Open in Portal',
    icon: LinkSquare02Icon,
    category: 'intelligence',
    cardType: 'minimal',
    hideInput: true,
    runningText: 'Opening in portal...',
    renderCard: renderPortalObjectPresentationCard,
  },
  export_session: {
    name: 'Session export',
    icon: File01Icon,
    category: 'intelligence',
    cardType: 'minimal',
    hideInput: true,
    runningText: 'Preparing export...',
    renderCard: renderCoPilotSessionExportCard,
  },
  render_visualization: {
    name: 'Chart',
    icon: ChartHistogramIcon,
    category: 'intelligence',
    cardType: 'minimal',
    hideInput: true,
    runningText: 'Building chart...',
    renderCard: renderVegaLiteVisualizationCard,
  },
  render_ad_graph: {
    name: 'AD Graph',
    icon: HierarchyIcon,
    category: 'intelligence',
    cardType: 'minimal',
    hideInput: true,
    runningText: 'Building AD graph...',
    renderCard: renderAdGraphVisualizationCard,
  },
  'ask-user': {
    name: 'Answer',
    icon: HelpCircleIcon,
    category: 'intelligence',
    cardType: 'minimal',
    hideInput: true,
    renderCard: renderAskUserAnswerCard,
  },
  mcp__terra__terra_query: {
    name: 'Data Lens',
    icon: SearchVisualIcon,
    category: 'intelligence',
    cardType: 'inline',
    runningText: 'Scanning data...',
    renderCard: renderDataQueryCard,
  },
  mcp__terra__terra_query_validate: {
    name: 'Query Preflight',
    icon: SecurityCheckIcon,
    category: 'intelligence',
    cardType: 'inline',
    runningText: 'Checking query...',
    renderCard: renderDataQueryCard,
  },
  mcp__terra__terra_query_schema: {
    name: 'Data Catalog',
    icon: LibraryIcon,
    category: 'intelligence',
    cardType: 'minimal',
    hideInput: true,
    hasExpandableContent: () => false,
  },
  mcp__terra__terra_external_network_attack_run: {
    name: 'External Attack Execution',
    icon: FlashIcon,
    category: 'executable',
    cardType: 'minimal',
    hideInput: true,
    renderCard: renderExternalAttackerCard,
  },
  mcp__terra__terra_external_network_signal_generation_run: {
    name: 'Signal Generation',
    icon: RadioIcon,
    category: 'executable',
    cardType: 'minimal',
    hideInput: true,
    renderCard: renderExternalNetworkSignalGenerationCard,
  },
  ...EXTERNAL_NETWORK_SCANNER_MCP_TOOL_CONFIGS,
  ...EXTERNAL_NETWORK_QUERY_MCP_TOOL_CONFIGS,
  ...EXTERNAL_NETWORK_SCOPE_MCP_TOOL_CONFIGS,
});
